import { useCallback, useEffect, useState } from "react";
import { NivelSituacaoTarefa } from "@workflow/types";

import { useAuth } from "@/lib/auth-context";
import { useSituacaoTarefas } from "@/hooks/tarefas/use-situacao-tarefas";

import { chaveUltimoAviso, hojeISO } from "./tarefas-vencidas.helpers";

/**
 * Kill switch geral: enquanto `false`, o aviso não aparece para ninguém e a
 * consulta de situação nem é disparada — nenhuma validação de tarefa vencida
 * afeta o usuário. Trocar para `true` religa o fluxo completo.
 */
const AVISO_TAREFAS_VENCIDAS_ATIVO = (import.meta.env.VITE_ENV as string) !== "local";

export interface AvisoTarefasVencidas {
  /** Deve exibir o aviso agora (há tarefas vencidas e ainda não avisou hoje). */
  aberto: boolean;
  /** Quantidade de tarefas vencidas. */
  quantidade: number;
  /** Nível de situação vindo do backend (orienta a intensidade da UI). */
  nivel: NivelSituacaoTarefa;
  /** Segundos que o botão de fechar fica travado após o texto aparecer. */
  segundosTravado: number;
  /** Marca que o aviso foi exibido hoje e fecha o dialog. */
  registrarVisto: () => void;
}

/**
 * Decide se o aviso de tarefas vencidas deve aparecer na sessão atual. A regra
 * de atraso (15/20/30 dias) vive no backend; aqui só combinamos o nível com a
 * marca diária em `localStorage` para exibir no máximo uma vez por dia por
 * pessoa. Não renderiza nada: a UI vive em `TarefasVencidasDialog`.
 */
export function useAvisoTarefasVencidas(): AvisoTarefasVencidas {
  const { user, isAuthenticated } = useAuth();
  const pessoaId = user?.id;

  const { data: situacao } = useSituacaoTarefas({
    enabled: AVISO_TAREFAS_VENCIDAS_ATIVO && isAuthenticated && !!pessoaId,
  });

  const nivel = situacao?.nivel ?? NivelSituacaoTarefa.NENHUM;
  const quantidade = situacao?.tarefasVencidas ?? 0;

  const [jaAvisouHoje, setJaAvisouHoje] = useState(true);

  useEffect(() => {
    if (!pessoaId) return;
    const marca = localStorage.getItem(chaveUltimoAviso(pessoaId));
    setJaAvisouHoje(marca === hojeISO());
  }, [pessoaId]);

  const registrarVisto = useCallback(() => {
    if (!pessoaId) return;
    localStorage.setItem(chaveUltimoAviso(pessoaId), hojeISO());
    setJaAvisouHoje(true);
  }, [pessoaId]);

  const aberto =
    AVISO_TAREFAS_VENCIDAS_ATIVO && !jaAvisouHoje && nivel !== NivelSituacaoTarefa.NENHUM;

  return {
    aberto,
    quantidade,
    nivel,
    segundosTravado: situacao?.segundosParaDesbloquear ?? 0,
    registrarVisto,
  };
}
